<template>
  <protected-pages-layout>
    <div class="wrapper">
      <!-- <navbar></navbar> -->
      <div v-if="!pageReady" class="d-flex justify-content-center align-items-center m-5">
        <div class="spinner-border text-dark" style="width: 7.5rem; height: 7.5rem" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <div v-else>
        <div class="container-fluid">
          <div class="row justify-content-center align-items-center w-100" style="margin: 30px 0">
            <div class="col-md-12">
              <div class="d-flex justify-content-center align-items-center">
                <div class="me-4" style="min-width: 200px">
                  <label for="periodSelect">Period</label>
                  <select v-model="timeSpentOnProjectParameters.period" class="form-select" id="periodSelect"
                    @change="loadData()" :disabled="!tableDataReady">
                    <option :key="index" :value="period.value" v-for="(period, index) in periods">
                      {{ period.name }}
                    </option>
                  </select>
                </div>
                <v-switch v-model="displayInHours" :label="displayInHours ? 'Display in hours' : 'Display in man-days'
                  " hide-details class="me-3"></v-switch>
                <button class="btn btn-outline-primary" @click="downloadCSVFileForLocalComputer()">
                  Download table data as CSV
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="container-fluid table-wrapper-tsoc">
          <v-card flat class="card border-0 shadow">
            <v-card-item class="sticky">
              <v-card-title>Time Spent By Client</v-card-title>
              <v-text-field v-model="searchQuery" prepend-inner-icon="mdi-magnify" label="Search" variant="outlined"
                single-line hide-details class="mx-4">
              </v-text-field>
            </v-card-item>
            <v-divider></v-divider>
            <v-card-text>
              <div class="table-container">
                <v-data-table-virtual :headers="headers" :items="TimeSpentOnProjectPerClient" :search="searchQuery"
                  :sort-by="sortBy" fixed-header :loading="!tableDataReady" @update:sort-by="sortBy = $event"
                  class="responsive-table" :item-height="48" :header-height="56" :height="900" density="compact"
                  :no-data-text="tableDataReady ? 'No data available' : ''">
                  <template #bottom></template>
                </v-data-table-virtual>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </div>
  </protected-pages-layout>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { storeToRefs } from "pinia";
import { periods } from "../assets/variables";
import { useClientReportStore } from "../stores/clientReport";
import _ from "lodash";

const clientReportStore = useClientReportStore();
const { TimeSpentOnProjectPerClient, timeSpentOnProjectParameters } =
  storeToRefs(clientReportStore);

const { fetchTimeSpentOnProjectPerClient } =
  clientReportStore;

const pageReady = ref(false);
const tableDataReady = ref(false);
const searchQuery = ref("");
const sortBy = ref([]);
const displayInHours = ref(false);

const numberWithCommas = (x) => {
  console.log(typeof x);
  if (typeof x === "undefined" || x === null) {
    return "0";
  }
  if (typeof x === "string") {
    x = parseFloat(x);
  }
  if (isNaN(x)) {
    return "0";
  }
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatValue = (value, shouldConvertToHours) => {
  if (typeof value === "undefined" || value === null) {
    return "0";
  }
  if (typeof value === "string") {
    value = parseFloat(value);
  }
  if (isNaN(value)) {
    return "0";
  }
  const converted = shouldConvertToHours ? value * 8 : value;
  return converted.toFixed(1);
};

const formatDate = (dateString) => {
  const parsedDate = new Date(dateString);
  return parsedDate
    .toLocaleDateString("en-GB", { dateStyle: "short" })
    .replace(/\//g, "-");
};

const headers = computed(() => [
  {
    title: "Client Name",
    key: "clientName",
    align: "center",
    sortable: true,
    width: "fit-content",
  },
  {
    title: "ARR",
    key: "arr",
    align: "center",
    sortable: true,
    width: "fit-content",
    value: (item) => numberWithCommas(item.arr),
  },
  {
    title: "Country/POD",
    key: "countryPod",
    align: "center",
    sortable: true,
    width: "fit-content",
  },
  {
    title: "CSM",
    key: "csmName",
    align: "center",
    sortable: true,
    width: "fit-content",
  },
  {
    title: "CS and PS Total",
    key: "csandPsTotal",
    align: "center",
    sortable: true,
    width: "fit-content",
    value: (item) => formatValue(item.csandPsTotal, displayInHours.value),
  },
  {
    title: "CS and PS Average",
    key: "csandPsAvg",
    align: "center",
    sortable: true,
    width: "fit-content",
    value: (item) => formatValue(item.csandPsAvg, displayInHours.value),
  },
  {
    title: "Onboarding Total",
    key: "onbardingTotal",
    align: "center",
    sortable: true,
    width: "fit-content",
    value: (item) => formatValue(item.onbardingTotal, displayInHours.value),
  },
  {
    title: "Onboarding Average",
    key: "onbardingAvg",
    align: "center",
    sortable: true,
    width: "fit-content",
    value: (item) => formatValue(item.onbardingAvg, displayInHours.value),
  },
  {
    title: "CS Total",
    key: "csTotal",
    align: "center",
    sortable: true,
    width: "fit-content",
    value: (item) => formatValue(item.CsTotal, displayInHours.value),
  },
  {
    title: "CS Avg",
    key: "csAvg",
    align: "center",
    sortable: true,
    width: "fit-content",
    value: (item) => formatValue(item.csAvg, displayInHours.value),
  },
  {
    title: "PS Total",
    key: "psTotal",
    align: "center",
    sortable: true,
    width: "fit-content",
    value: (item) => formatValue(item.psTotal, displayInHours.value),
  },
  {
    title: "PS Avg",
    key: "psAvg",
    align: "center",
    sortable: true,
    width: "fit-content",
    value: (item) => formatValue(item.psAvg, displayInHours.value),
  },
  {
    title: "Support Total",
    key: "supportTotal",
    align: "center",
    sortable: true,
    width: "fit-content",
    value: (item) => formatValue(item.supportTotal, displayInHours.value),
  },
  {
    title: "Support Avg",
    key: "supportAvg",
    align: "center",
    width: "fit-content",
    sortable: true,
    value: (item) => formatValue(item.supportAvg, displayInHours.value),
  },
  {
    title: "Analyze Start Date",
    key: "analyzeStartDate",
    align: "center",
    width: "fit-content",
    value: (item) => formatDate(item.analyzeStartDate),
  },
  {
    title: "Analyze End Date",
    key: "analyzeEndDate",
    align: "center",
    width: "fit-content",
    value: (item) => formatDate(item.analyzeEndDate),
  },
]);

let timeout;

watch(searchQuery, (newQuery) => {
  clearTimeout(timeout);
  timeout = setTimeout(() => {
    // page.value = 1;
    loadData();
  }, 500);
});

const loadData = async () => {
  tableDataReady.value = false;
  try {
    await fetchTimeSpentOnProjectPerClient();
  } finally {
    tableDataReady.value = true;
  }
};

onMounted(async () => {
  try {
    await fetchTimeSpentOnProjectPerClient();

    if (!Array.isArray(TimeSpentOnProjectPerClient.value)) {
      console.error(
        "TimeSpentOnProjectPerClient is not an array:",
        TimeSpentOnProjectPerClient.value
      );
      TimeSpentOnProjectPerClient.value = [];
    }
  } catch (error) {
    console.error("Error loading data:", error);
    TimeSpentOnProjectPerClient.value = [];
  } finally {
    pageReady.value = true;
    tableDataReady.value = true;
  }
});

const castArrayIntoCSV = () => {
  if (TimeSpentOnProjectPerClient.value.length === 0) {
    return "";
  }

  const data = TimeSpentOnProjectPerClient.value;
  const keys = Object.keys(data[0]);
  const convertibleFields = [
    "csandPsTotal", "csandPsAvg", "onbardingTotal", "onbardingAvg",
    "csTotal", "csAvg", "psTotal", "psAvg", "supportTotal", "supportAvg"
  ];

  let result = keys.join(",") + "\n";

  for (const item of data) {
    const row = keys.map((key) => {
      if (displayInHours.value && convertibleFields.includes(key)) {
        return (item[key] * 8).toFixed(1);
      }
      return item[key];
    }).join(",");
    result += row + "\n";
  }

  return result;
};

const downloadCSVFileForLocalComputer = () => {
  const csvData = castArrayIntoCSV();
  const blob = new Blob([csvData], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();
  a.download = `TimeSpentOnProjectPerClient_${dd}-${mm}-${yyyy}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
};
</script>

<script>
import Navbar from "../components/Navbar.vue";
import ExportModule from "../components/ExportModule.vue";
import { defineComponent } from "vue";
import { ref } from "vue";

export default {
  name: "TimeSpentOnClientsByProjects",
  components: {
    navbar: Navbar,
    ExportModule,
  },
};
</script>

<style lang="scss">
.wrapper {
  width: clamp(867px,
      calc(100vw * 0.8465),
      2166px);
  aspect-ratio: 1219/811;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
}

.table-wrapper-tsoc {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  contain: layout style paint;
}

.table-wrapper-tsoc .v-card-text,
.table-wrapper-tsoc .v-data-table-virtual {
  flex: 1;
  overflow-y: auto;
  min-width: 0;
  will-change: scroll-position;
  /* critical so long columns wrap instead of forcing overflow */
}

.table-wrapper-tsoc .v-data-table-virtual__table {
  width: 100%;
  table-layout: fixed;
  contain: layout;
}

.table-wrapper-tsoc th,
.table-wrapper-tsoc td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transform: translateZ(0);
}
.v-data-table-virtual__container {
  transform: translateZ(0);
  backface-visibility: hidden;
}

.table-scroll {
  width: 100%;
  overflow-y: scroll;
  max-height: max-content;
}
</style>
